import { useRef } from 'react'
import { FileText, Image as ImageIcon, Paperclip, X } from 'lucide-react'
import { Button as ButtonComponent } from '@/components/ui/button'
import { Label as LabelComponent } from '@/components/ui/label'
import { ButtonProps } from '@/types'

// Type casts for the UI components from JSX files
const Button = ButtonComponent as React.FC<
    ButtonProps & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'aria-label' | 'aria-describedby'>
>
const Label = LabelComponent as React.FC<React.LabelHTMLAttributes<HTMLLabelElement>>

// Mirrors backend/services/ai/brand-files.js, which enforces them
export const MAX_BRAND_FILES = 5
export const MAX_BRAND_FILE_SIZE = 10 * 1024 * 1024
export const MAX_BRAND_FILES_TOTAL_SIZE = 14 * 1024 * 1024

const MB = 1024 * 1024

export interface LicenseBrandFile {
    id: string
    name: string
    mimeType: string
    size: number
    url: string
}

const extensionOf = (name: string) => name.split('.').pop()?.toLowerCase() ?? ''

export const formatFileSize = (bytes: number) =>
    bytes >= MB ? `${(bytes / MB).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`

const IMAGE_TYPES: Record<string, string> = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    webp: 'image/webp',
}

/** An upload slot of a license overview. Mirrors SLOTS in backend/services/ai/brand-files.js, which enforces it. */
export interface FileSlot {
    label: string
    hint: string
    listLabel: string
    addLabel: string
    maxFiles: number
    maxFileSize: number
    maxTotalSize: number
    types: Record<string, string>
    messages: { wrongType: string; fileTooLarge: string; tooMany: string; totalTooLarge: string }
}

export const BRAND_FILES_SLOT: FileSlot = {
    label: 'Brand files',
    hint: `Style guides and other brand images. PNG, JPG, WebP or PDF, up to ${MAX_BRAND_FILES} files of ${
        MAX_BRAND_FILE_SIZE / MB
    } MB each and ${MAX_BRAND_FILES_TOTAL_SIZE / MB} MB together.`,
    listLabel: 'Selected brand files',
    addLabel: 'Add files',
    maxFiles: MAX_BRAND_FILES,
    maxFileSize: MAX_BRAND_FILE_SIZE,
    maxTotalSize: MAX_BRAND_FILES_TOTAL_SIZE,
    types: { ...IMAGE_TYPES, pdf: 'application/pdf' },
    messages: {
        wrongType: 'Upload PNG, JPG or WebP images, or PDF style guides.',
        fileTooLarge: `Each brand file must be ${MAX_BRAND_FILE_SIZE / MB} MB or smaller.`,
        tooMany: `Upload at most ${MAX_BRAND_FILES} brand files.`,
        totalTooLarge: `Brand files must be ${MAX_BRAND_FILES_TOTAL_SIZE / MB} MB or smaller together.`,
    },
}

export const LOGO_SLOT: FileSlot = {
    label: 'Logo',
    hint: 'Added to the corner of every AI image. A transparent PNG works best. PNG, JPG or WebP, up to 5 MB.',
    listLabel: 'Selected logo',
    addLabel: 'Add logo',
    maxFiles: 1,
    maxFileSize: 5 * MB,
    maxTotalSize: 5 * MB,
    types: IMAGE_TYPES,
    messages: {
        wrongType: 'Upload the logo as a PNG, JPG or WebP image.',
        fileTooLarge: 'The logo must be 5 MB or smaller.',
        tooMany: 'Upload one logo.',
        totalTooLarge: 'The logo must be 5 MB or smaller.',
    },
}

const acceptOf = (slot: FileSlot) =>
    [...Object.keys(slot.types).map((extension) => `.${extension}`), ...new Set(Object.values(slot.types))].join(',')

const isAccepted = (slot: FileSlot, file: File) =>
    Object.values(slot.types).includes(file.type) || Boolean(slot.types[extensionOf(file.name)])

/** Why the files of a slot cannot be saved together, or null when they can, counting the saved files kept. */
export const filesError = (slot: FileSlot, kept: LicenseBrandFile[], added: File[]): string | null => {
    if (added.some((file) => !isAccepted(slot, file))) return slot.messages.wrongType
    if (added.some((file) => file.size > slot.maxFileSize)) return slot.messages.fileTooLarge
    if (kept.length + added.length > slot.maxFiles) return slot.messages.tooMany
    const total = [...kept, ...added].reduce((sum, { size }) => sum + size, 0)
    if (total > slot.maxTotalSize) return slot.messages.totalTooLarge
    return null
}

interface FileRowProps {
    name: string
    mimeType: string
    size: number
    href?: string
    disabled: boolean
    onRemove: () => void
}

const FileRow = ({ name, mimeType, size, href, disabled, onRemove }: FileRowProps) => {
    const Icon = mimeType === 'application/pdf' ? FileText : ImageIcon
    return (
        <li className="tw:flex tw:items-center tw:gap-2 tw:border tw:border-gray-600 tw:px-2 tw:py-1">
            <Icon size={16} className="tw:shrink-0 tw:text-gray-400" aria-hidden="true" />
            {href ? (
                <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="tw:truncate tw:text-sm tw:text-foreground tw:hover:underline"
                >
                    {name}
                </a>
            ) : (
                <span className="tw:truncate tw:text-sm tw:text-foreground">{name}</span>
            )}
            <span className="tw:shrink-0 tw:text-xs tw:text-gray-400">{formatFileSize(size)}</span>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                className="tw:ml-auto tw:size-7 tw:shrink-0"
                aria-label={`Remove ${name}`}
                onClick={onRemove}
                disabled={disabled}
            >
                <X size={14} />
            </Button>
        </li>
    )
}

interface BrandFilesFieldProps {
    id: string
    slot?: FileSlot
    kept: LicenseBrandFile[]
    added: File[]
    error?: string | null
    disabled?: boolean
    onAdd: (files: File[]) => void
    onRemoveKept: (id: string) => void
    onRemoveAdded: (index: number) => void
}

/** Saved files of an upload slot to keep and new files to upload on save. */
const BrandFilesField = ({
    id,
    slot = BRAND_FILES_SLOT,
    kept,
    added,
    error,
    disabled = false,
    onAdd,
    onRemoveKept,
    onRemoveAdded,
}: BrandFilesFieldProps) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const full = kept.length + added.length >= slot.maxFiles
    const describedBy = [`${id}-hint`, full && `${id}-full`, error && `${id}-error`].filter(Boolean).join(' ')

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? [])
        // Cleared so the same file can be picked again after it is removed
        event.target.value = ''
        if (files.length > 0) onAdd(files)
    }

    return (
        <div className="tw:flex tw:flex-col tw:gap-1">
            <Label htmlFor={id}>{slot.label}</Label>
            <span id={`${id}-hint`} className="tw:text-xs tw:text-gray-400">
                {slot.hint}
            </span>
            {/* Out of the tab order: the "Add files" button opens the picker and is the visible control */}
            <input
                ref={inputRef}
                id={id}
                type="file"
                multiple={slot.maxFiles > 1}
                accept={acceptOf(slot)}
                tabIndex={-1}
                className="tw:sr-only"
                aria-describedby={describedBy}
                aria-invalid={Boolean(error)}
                onChange={handleChange}
                disabled={disabled || full}
            />

            {(kept.length > 0 || added.length > 0) && (
                <ul
                    aria-label={slot.listLabel}
                    className="tw:flex tw:flex-col tw:gap-1 tw:list-none tw:!p-0 tw:!mb-2 tw:!mt-1"
                >
                    {kept.map((file) => (
                        <FileRow
                            key={file.id}
                            name={file.name}
                            mimeType={file.mimeType}
                            size={file.size}
                            href={import.meta.env.VITE_S3_BUCKET_URL + file.url}
                            disabled={disabled}
                            onRemove={() => onRemoveKept(file.id)}
                        />
                    ))}
                    {added.map((file, index) => (
                        <FileRow
                            key={`${file.name}-${file.lastModified}-${index}`}
                            name={file.name}
                            mimeType={slot.types[extensionOf(file.name)] ?? file.type}
                            size={file.size}
                            disabled={disabled}
                            onRemove={() => onRemoveAdded(index)}
                        />
                    ))}
                </ul>
            )}

            <Button
                type="button"
                variant="secondary"
                size="sm"
                className="tw:self-start tw:mt-1"
                onClick={() => inputRef.current?.click()}
                disabled={disabled || full}
                aria-describedby={describedBy}
            >
                <Paperclip size={14} />
                {slot.addLabel}
            </Button>

            {full && (
                <span id={`${id}-full`} className="tw:text-xs tw:text-gray-400">
                    You have added the maximum of {slot.maxFiles} {slot.maxFiles === 1 ? 'file' : 'files'}. Remove{' '}
                    {slot.maxFiles === 1 ? 'it' : 'one'} to add another.
                </span>
            )}

            {error && (
                <p id={`${id}-error`} role="alert" className="tw:text-sm tw:text-red-400 tw:m-0">
                    {error}
                </p>
            )}
        </div>
    )
}

export default BrandFilesField
